import React, { useEffect, useMemo, useState } from "react";
import List from "@mui/material/List";
import { useLocation } from "react-router-dom";
import routesConfig, { RouterConfigData } from "../../../../../pages/routesConfig";
import NavVerticalGroup from "./VerticalNavGroup";
import VerticalCollapse from "./VerticalCollapse";
import VerticalItem from "./VerticalItem";

const filterRoutesByModules = (routes: RouterConfigData[], allowedModules: string[]): RouterConfigData[] => {
  const filterRecursive = (items: RouterConfigData[]): RouterConfigData[] => {
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterRecursive(item.children);
          if (filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }
          return null;
        } else if (
          allowedModules.includes(item.title) ||
          item.id?.startsWith("investorDesk") ||
          item.url?.startsWith("/investorDesk")
        ) {
          return item;
        }
        return null;
      })
      .filter((item): item is RouterConfigData => item !== null);
  };

  return filterRecursive(routes);
};

type CollapseState = { [level: number]: string | null };

const areCollapseStatesEqual = (a: CollapseState, b: CollapseState) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (a[key as any] !== b[key as any]) return false;
  }
  return true;
};

const VerticalNav = () => {
  const [activeCollapseIds, setActiveCollapseIds] = useState<CollapseState>({});
  const location = useLocation();

  const userModules = useMemo(
    () => JSON.parse(localStorage.getItem("userModules") || "[]"),
    []
  );

  const allowedModuleNames = useMemo(
    () => userModules.map((module: any) => module.module_name),
    [userModules]
  );

  const filteredRoutes = useMemo(
    () => filterRoutesByModules(routesConfig, allowedModuleNames),
    [allowedModuleNames]
  );

  const handleToggleCollapse = (level: number, id: string) => {
    setActiveCollapseIds((prev) => ({
      ...prev,
      [level]: prev[level] === id ? null : id,
    }));
  };

  useEffect(() => {
    const nextState: CollapseState = {};

    const openParentsByPath = (
      routes: RouterConfigData[],
      path: string,
      level = 0
    ): string | null => {
      for (const item of routes) {
        if (item.children) {
          const childMatch = openParentsByPath(item.children, path, level + 1);
          if (childMatch) {
            nextState[level] = item.id;
            return item.id;
          }
        } else if (item.url && path.startsWith(item.url)) {
          return item.id;
        }
      }
      return null;
    };

    openParentsByPath(filteredRoutes, location.pathname);

    setActiveCollapseIds((prev) => {
      if (areCollapseStatesEqual(prev, nextState)) {
        return prev; // no change → no extra render
      }
      return nextState;
    });
  }, [location.pathname, filteredRoutes]);

  return (
    <List sx={{ position: "relative", padding: 0 }} component="div">
      {filteredRoutes.map((item: RouterConfigData) => (
        <React.Fragment key={item.id}>
          {item.type === "group" && (
            <NavVerticalGroup
              item={item}
              level={0}
              activeCollapseIds={activeCollapseIds}
              handleToggleCollapse={handleToggleCollapse}
            />
          )}

          {item.type === "collapse" && (
            <VerticalCollapse
              item={item}
              level={0}
              activeCollapseIds={activeCollapseIds}
              handleToggleCollapse={handleToggleCollapse}
            />
          )}

          {item.type === "item" && <VerticalItem item={item} level={0} />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default VerticalNav;
